// @flow
/* eslint max-lines: 0 */

import EventEmitter from 'events';
import Cookies from 'js-cookie';
import getUserManager from '../utils/getUserManager';
import getIdentityServerConfig from '../constants/getIdentityServerConfig';
import getURLParam from '../utils/getURLParam';
import logger from '../utils/logger';

export const AuthEvent = {
  AUTH_FAIL: 'authFail',
  USER_LOADED: 'userLoaded',
  AUTH_REDIRECT_TO_LOGIN_FAIL: 'authRedirectToLoginFail',
  COOKIE_SET: 'cookieSet',
};

type User = {
  profile: { sub: string },
  expired: boolean,
};

/**
 * Handles user authentication.
 */
export class Auth extends EventEmitter {
  /**
   * Initialize the class.
   */
  constructor() {
    super();
    this.userManager = getUserManager();
    this.config = getIdentityServerConfig();
    this.userManager.events.addUserLoaded(user => this.onUserloaded(user));
    this.userManager.events.addUserSignedOut(() => {
      this.logout();
    });

    (this: any).postLoginAuthentication = (this: any).postLoginAuthentication.bind(this);
    (this: any).postLoginInit = (this: any).postLoginInit.bind(this);
    (this: any).logout = (this: any).logout.bind(this);
    (this: any).onUserloaded = (this: any).onUserloaded.bind(this);
    (this: any).addPublicSilentLoginFunction = (this: any).addPublicSilentLoginFunction.bind(this);
  }

  webStorage: any = null;
  userManager: any = null;
  config: any = null;
  silentLoginPromise: Promise<*> | null;

  /**
   * Perform a silent login, set the unsecure cookie and swap for a secure cookie.
   *
   * @param {Boolean} throwOnError Indicates whether to throw an error when unable to perform silentLogin
   *
   * @return {Promise<void>} This will return a promise which will be rejected if there is an error
   */
  silentLogin(throwOnError: boolean = false): Promise<*> {
    if (this.silentLoginPromise) return this.silentLoginPromise;
    this.silentLoginPromise = this.userManager
      .signinSilent()
      .then(user => {
        this.silentLoginPromise = null;
        this.silentLoginCookieSwap(user);
      })
      .catch(error => {
        this.silentLoginPromise = null;
        logger.error('Unable to perform silentLogin', error);
        if (throwOnError) {
          throw error;
        } else {
          this.emit(AuthEvent.AUTH_FAIL);
        }
      });
    return this.silentLoginPromise;
  }

  /**
   * @param {Object} user the user
   * @return {Void} No return value
   */
  onUserloaded(user: any) {
    this.emit('userLoaded', user);
  }

  /**
   * Handle the 'just logged in' flow from IdentityServer when url is /loggedIn
   *
   * @return {Void} No return value
   */
  postLoginAuthentication() {
    const userHasCancelled = getURLParam('error') === 'access_denied';
    if (userHasCancelled) {
      window.location.replace('https://www.icis.com/explore/');
      return;
    }

    this.userManager
      .signinRedirectCallback()
      .then(user => this.postLoginInit(user))
      .catch(error => {
        logger.error(
          `Unable to perform post login initialization - redirecting to login page`,
          error
        );
        this.logout();
      });
  }

  /**
   * Handle the silent login redirection from the OAuth server.
   *
   * @return {void} No return value
   */
  silentLoginAuthentication() {
    this.userManager.signinSilentCallback(window.location.href, '*');
  }

  /**
   * Set the unsecure cookie and swap for a secure cookie.
   *
   * @param {Object} user User returned from IDS
   * @return {Promise<void>} This will return a promise which will be rejected if there is an error
   */
  silentLoginCookieSwap(user: Object): Promise<void> {
    if (user && user.access_token) {
      Cookies.set(this.config.unsecuredAccessTokenCookie, user.access_token, {
        domain: this.config.authDomain,
      });

      return fetch(this.config.secureAccessTokenEndpoint, {
        method: 'GET',
        credentials: 'include',
      })
        .then((response: Response) => {
          if (!response.ok) {
            logger.error(
              `Unable to get secure access token: ${response.status} - ${response.statusText}`
            );
            throw response.statusText;
          }
          this.emit(AuthEvent.COOKIE_SET);
        })
        .catch(err => {
          logger.error('Error fetching from secureAccessTokenEndpoint', err);
          this.emit(AuthEvent.AUTH_FAIL);
          throw err;
        });
    }

    this.emit(AuthEvent.AUTH_FAIL);
    logger.error('Unable to perform silent login authentication: no access token');
    return Promise.reject(Error('Unable to perform silent login authentication: no access token'));
  }

  /**
   * Redirect to Home.
   * @param {Object} user User returned from IDS
   * @return {Void} No return value
   */
  postLoginInit(user: Object) {
    if (user && user.access_token) {
      Cookies.set(this.config.unsecuredAccessTokenCookie, user.access_token, {
        domain: this.config.authDomain,
      });

      let targetUrl = (user && user.state) || '/';

      if (!targetUrl.startsWith('/')) {
        logger.error(
          `Invalid target returned from login - only relative addresses allowed.  ${targetUrl}`
        );
        targetUrl = '/';
      }

      /**
       * Handles an error from the server when requesting a secure access token.
       *
       * @param {Object} error The response from the server.
       * @return {Void} nothing is returned.
       */
      const handleCookieRetrievalError = error => {
        logger.error(`Unable to get secure access token: ${error}`, error);
        this.userManager.signoutRedirect();
      };

      /**
       * Handles the response from the server when requesting a secure access token.
       *
       * @param {Object} response The response from the server.
       * @return {Void} nothing is returned.
       */
      const handleCookieRetrievalResponse = response => {
        if (!response.ok) handleCookieRetrievalError(`${response.status} - ${response.statusText}`);
        this.emit(AuthEvent.COOKIE_SET);
        response
          .json()
          .then(responseData => {
            if (targetUrl === '/') window.location.assign(responseData);
            else window.location.assign(targetUrl);
          })
          .catch(() => {
            window.location.assign(targetUrl);
          });
      };

      fetch(this.config.secureAccessTokenEndpoint, { method: 'GET', credentials: 'include' })
        .then(handleCookieRetrievalResponse)
        .catch(handleCookieRetrievalError);
    }
  }

  /**
   * Redirects the user to the login page.
   *
   * @return {Void} nothing is returned.
   */
  redirectToLogin() {
    const targetRelativePath = window.location.pathname + window.location.search;
    this.userManager.signinRedirect({ data: targetRelativePath }).catch(error => {
      logger.error('Unable to perform redirectToLogin', error);
      this.emit(AuthEvent.AUTH_REDIRECT_TO_LOGIN_FAIL);
    });
  }

  /**
   * Logs the user out.
   *
   * @return {Void} nothing is returned.
   */
  logout() {
    /**
     * Signs the user out using the appropriate redirect URL depending on whether
     * they are internal or using the shared header.
     *
     * @return {Void} Nothing is returned.
     */
    const signout = () => {
      if (this.config.toggles.isExternal) {
        window.location.assign(`${this.config.externalLogoutUrl}`);
        return;
      }
      this.userManager.signoutRedirect();
    };

    fetch(this.config.secureAccessTokenRemovalEndpoint, {
      method: 'GET',
      credentials: 'include',
    })
      .then(() => {
        signout();
      })
      .catch(error => {
        logger.error('Could not remove the secure access token:', error);
        signout();
      });
  }

  /**
   * Logs out users who clicked logout on the shared header on another site.
   *
   * @return {Void} nothing is returned.
   */
  externalLogout() {
    this.silentLogin().then(() => {
      this.logout();
    });
  }

  /**
   * Retrieves the current user's details.
   *
   * @return {Object} The current user's details.
   */
  getUser(): Promise<?User> {
    if (this.config.toggles.useMockBff) {
      return Promise.resolve({
        profile: { sub: 'DUMMY_VALUE' },
        expired: false,
      });
    }
    return this.userManager.getUser();
  }

  /**
   * Adds a function to window which when called will perform a silent login.
   *
   * @return {Void} nothing is returned.
   */
  addPublicSilentLoginFunction() {
    window.icisSharedSilentLogin = (): Promise<*> =>
      this.silentLogin(true).catch(() => this.redirectToLogin());
  }
}

let auth;

/**
 * Gets an singleton of Auth.
 *
 * @return {Auth} An auth singleton.
 */
const getAuth = () => {
  if (auth) return auth;
  auth = new Auth();
  return auth;
};

export default getAuth;
